import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, GitBranch, Lock, Plus, Trash2 } from "lucide-react";

import {
  useAddEstimateLine,
  useCreateEstimateVersion,
  useEstimateVersion,
  useFinalizeEstimateVersion,
  useProjectEstimate,
  useRemoveEstimateLine,
} from "@/api/queries";
import type {
  EstimateLine,
  EstimateLineType,
  EstimateVersion,
  EstimateVersionDetail,
  UnitCode,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatEstimateLineType,
  formatEstimateVersionStatus,
  formatMoney,
  formatProjectEstimateStatus,
  formatQuantity,
  formatUnit,
  formatWaste,
  pluralize,
} from "@/lib/format";
import { estimateVersionTone, projectEstimateTone } from "@/lib/stock";
import { STOCKABLE_UNITS } from "@/lib/units";

const LINE_TYPES: EstimateLineType[] = [
  "MATERIAL",
  "LABOUR",
  "EQUIPMENT",
  "SUBCONTRACT",
  "OTHER",
];

/**
 * One estimate, one version at a time (V1.6-01).
 *
 * Two things the screen must never let happen: editing a finalized version, and
 * leaving somebody unsure which version they are looking at. So the version
 * picker is always visible, the current one is named, and a frozen version
 * renders read-only rather than failing on save.
 */
export function ProjectEstimateDetailPage() {
  const { estimateId = "" } = useParams();
  const { data, isLoading, error, refetch } = useProjectEstimate(estimateId);
  const [viewing, setViewing] = useState<string | null>(null);

  const olderVersion = useEstimateVersion(
    estimateId,
    viewing && viewing !== data?.current?.version.id ? viewing : undefined,
  );

  if (isLoading) return <LoadingState label="Ouverture de l'estimation…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { estimate, versions, current } = data;
  const shown: EstimateVersionDetail | null =
    viewing && viewing !== current?.version.id ? (olderVersion.data ?? null) : current;

  return (
    <Page>
      <PageHeader
        title={estimate.name}
        subtitle={`${estimate.code}${estimate.description ? ` · ${estimate.description}` : ""}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/estimates"
              className="hidden items-center gap-1.5 rounded-md px-2 py-2 text-[13px] text-ink-subtle hover:text-ink md:flex"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Estimations
            </Link>
            <StatusBadge tone={projectEstimateTone(estimate.status)}>
              {formatProjectEstimateStatus(estimate.status)}
            </StatusBadge>
          </div>
        }
      />

      <VersionPicker
        versions={versions}
        currentId={current?.version.id ?? null}
        viewingId={shown?.version.id ?? null}
        onView={setViewing}
      />

      {shown ? (
        <VersionPanel estimateId={estimateId} detail={shown} />
      ) : (
        <EmptyState title="Aucune version" description="Cette estimation n'a pas encore de V1." />
      )}
    </Page>
  );
}

/**
 * Which version am I looking at?
 *
 * The current one is labelled as such and the others say plainly that they are
 * past. A revision that looks like the live sheet is how somebody quotes a
 * price that stopped being true in March.
 */
function VersionPicker({
  versions,
  currentId,
  viewingId,
  onView,
}: {
  versions: EstimateVersion[];
  currentId: string | null;
  viewingId: string | null;
  onView: (id: string) => void;
}) {
  if (versions.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <span className="text-[12px] text-ink-subtle">
        {pluralize(versions.length, "version")} :
      </span>
      {[...versions]
        .sort((a, b) => b.version_number - a.version_number)
        .map((version) => {
          const isCurrent = version.id === currentId;
          const isViewing = version.id === viewingId;
          return (
            <button
              key={version.id}
              type="button"
              onClick={() => onView(version.id)}
              aria-current={isViewing ? "true" : undefined}
              className={[
                "flex min-h-[38px] items-center gap-1.5 rounded-md border px-3 text-[13px]",
                isViewing
                  ? "border-brand bg-brand-subtle text-ink"
                  : "border-border bg-surface text-ink-muted hover:text-ink",
              ].join(" ")}
            >
              <span className="tnum font-medium">V{version.version_number}</span>
              {version.status === "FINALIZED" && <Lock className="size-3.5" aria-hidden />}
              <span className="text-[11px] text-ink-subtle">
                {isCurrent ? "courante" : "précédente"}
              </span>
            </button>
          );
        })}
    </div>
  );
}

function VersionPanel({
  estimateId,
  detail,
}: {
  estimateId: string;
  detail: EstimateVersionDetail;
}) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const finalize = useFinalizeEstimateVersion();
  const newVersion = useCreateEstimateVersion();

  const { version, lines, totals, is_current: isCurrent } = detail;
  const frozen = version.status === "FINALIZED";
  const editable = isCurrent && !frozen;

  async function freeze() {
    try {
      await finalize.mutateAsync({ estimateId, versionId: version.id });
      toast.success(`V${version.version_number} figée`, "Ses lignes ne bougeront plus.");
    } catch (error) {
      toast.error("Version non figée", String(error));
    }
  }

  async function revise(copyLines: boolean) {
    try {
      const created = await newVersion.mutateAsync({ estimateId, copy_lines: copyLines });
      toast.success(
        `V${created.version.version_number} ouverte`,
        copyLines ? "Les lignes ont été reprises." : "Nouvelle version vide.",
      );
    } catch (error) {
      toast.error("Version non créée", String(error));
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
          <span className="text-[15px] font-medium text-ink">V{version.version_number}</span>
          <StatusBadge tone={estimateVersionTone(version.status)}>
            {formatEstimateVersionStatus(version.status)}
          </StatusBadge>
          {frozen && version.finalized_at && (
            <span>
              Figée le {formatDate(version.finalized_at)}
              {version.finalized_by ? ` par ${version.finalized_by}` : ""}
            </span>
          )}
          {!isCurrent && <span>Version précédente — lecture seule.</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {editable && (
            <>
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                <Plus className="size-4" aria-hidden />
                Ajouter une ligne
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void freeze()}
                loading={finalize.isPending}
                disabled={lines.length === 0}
              >
                <Lock className="size-4" aria-hidden />
                Figer
              </Button>
            </>
          )}
          {isCurrent && frozen && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void revise(true)}
                loading={newVersion.isPending}
              >
                <GitBranch className="size-4" aria-hidden />
                Nouvelle version (reprendre)
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void revise(false)}
                loading={newVersion.isPending}
              >
                Nouvelle version vide
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-surface">
        {lines.length === 0 ? (
          <EmptyState
            title="Version vide"
            description="Ajoutez du matériel, de la main-d'œuvre, de l'équipement ou de la sous-traitance."
          />
        ) : (
          <>
            <div className="hidden md:block">
              <TableShell>
                <thead>
                  <tr>
                    <Th className="w-36 whitespace-nowrap">Type</Th>
                    <Th className="max-w-0">Poste</Th>
                    <Th align="right" className="w-28 whitespace-nowrap">
                      Quantité
                    </Th>
                    <Th align="right" className="hidden w-24 whitespace-nowrap @3xl:table-cell">
                      Perte
                    </Th>
                    <Th align="right" className="hidden w-28 whitespace-nowrap @3xl:table-cell">
                      Retenue
                    </Th>
                    <Th align="right" className="w-28 whitespace-nowrap">
                      Coût unit.
                    </Th>
                    <Th align="right" className="w-32 whitespace-nowrap">
                      Total
                    </Th>
                    <Th className="w-12" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <LineRow
                      key={line.id}
                      estimateId={estimateId}
                      versionId={version.id}
                      line={line}
                      editable={editable}
                    />
                  ))}
                </tbody>
              </TableShell>
            </div>

            <ul className="divide-y divide-border/60 md:hidden">
              {lines.map((line) => (
                <li key={line.id}>
                  <LineCard line={line} />
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="flex flex-col gap-1 border-t border-border p-3 text-[13px]">
          {Object.entries(totals.by_type).map(([type, amount]) => (
            <div key={type} className="flex items-center justify-between text-ink-muted">
              <span>{formatEstimateLineType(type)}</span>
              <span className="tnum">{formatMoney(amount)}</span>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between border-t border-border pt-2 text-[15px] font-medium text-ink">
            <span>Total</span>
            <span className="tnum">{formatMoney(totals.total)}</span>
          </div>
          {totals.unpriced_lines > 0 && (
            <p className="text-[12px] text-warning">
              {pluralize(totals.unpriced_lines, "ligne")} sans prix — le total ne les compte pas.
            </p>
          )}
        </div>
      </div>

      {editable && (
        <div className="md:hidden">
          <Button variant="primary" className="w-full" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden />
            Ajouter une ligne
          </Button>
        </div>
      )}

      <AddLineDialog
        open={adding}
        onOpenChange={setAdding}
        estimateId={estimateId}
        versionId={version.id}
      />
    </>
  );
}

function LineRow({
  estimateId,
  versionId,
  line,
  editable,
}: {
  estimateId: string;
  versionId: string;
  line: EstimateLine;
  editable: boolean;
}) {
  const toast = useToast();
  const remove = useRemoveEstimateLine();

  return (
    <Tr>
      <Td className="whitespace-nowrap text-[13px] text-ink-muted">
        {formatEstimateLineType(line.line_type)}
      </Td>
      <Td className="max-w-0">
        <span className="block truncate font-medium text-ink">{line.description}</span>
        {line.note && <span className="block truncate text-[11px] text-ink-subtle">{line.note}</span>}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatQuantity(line.quantity)} {line.unit ? formatUnit(line.unit) : ""}
      </Td>
      <Td
        align="right"
        className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell"
      >
        {formatWaste(line.waste_factor)}
      </Td>
      <Td
        align="right"
        className="tnum hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell"
        title="Quantité à prévoir, perte comprise"
      >
        {formatQuantity(line.effective_quantity)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
        {formatMoney(line.unit_cost)}
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {line.unpriced ? <span className="text-warning">à chiffrer</span> : formatMoney(line.line_total)}
      </Td>
      <Td>
        {editable && (
          <button
            type="button"
            aria-label={`Retirer ${line.description}`}
            onClick={() =>
              remove.mutate(
                { estimateId, versionId, lineId: line.id },
                { onError: (error) => toast.error("Ligne non retirée", String(error)) },
              )
            }
            className="rounded-md p-2 text-ink-subtle hover:text-danger"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        )}
      </Td>
    </Tr>
  );
}

function LineCard({ line }: { line: EstimateLine }) {
  return (
    <div className="flex items-start gap-3 px-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{line.description}</p>
        <p className="text-[12px] text-ink-muted">
          {formatEstimateLineType(line.line_type)} · {formatQuantity(line.quantity)}{" "}
          {line.unit ? formatUnit(line.unit) : ""}
          {line.waste_factor ? ` · perte ${formatWaste(line.waste_factor)}` : ""}
        </p>
      </div>
      <span className="tnum shrink-0 text-[14px]">
        {line.unpriced ? (
          <span className="text-[12px] text-warning">à chiffrer</span>
        ) : (
          formatMoney(line.line_total)
        )}
      </span>
    </div>
  );
}

function AddLineDialog({
  open,
  onOpenChange,
  estimateId,
  versionId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimateId: string;
  versionId: string;
}) {
  const toast = useToast();
  const addLine = useAddEstimateLine();
  const [lineType, setLineType] = useState<EstimateLineType>("MATERIAL");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState<UnitCode>("EA");
  const [unitCost, setUnitCost] = useState("");
  const [wastePercent, setWastePercent] = useState("");
  const [allowance, setAllowance] = useState("");

  const isMaterial = lineType === "MATERIAL";

  function reset() {
    setLineType("MATERIAL");
    setDescription("");
    setQuantity("1");
    setUnit("EA");
    setUnitCost("");
    setWastePercent("");
    setAllowance("");
  }

  async function submit() {
    if (!description.trim() || !quantity) return;
    // Saisi en pourcentage parce que c'est ce qu'un estimateur dit ; envoyé en
    // fraction parce que c'est ce que le backend stocke. La conversion vit ici
    // et nulle part ailleurs.
    const waste =
      isMaterial && wastePercent.trim() ? String(Number(wastePercent) / 100) : null;
    try {
      await addLine.mutateAsync({
        estimateId,
        versionId,
        line_type: lineType,
        description,
        quantity,
        unit: isMaterial ? unit : null,
        unit_cost: unitCost.trim() || null,
        waste_factor: waste,
        allowance: allowance.trim() || null,
      });
      toast.success("Ligne ajoutée");
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Ligne non ajoutée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ajouter une ligne"
      description="Matériel, main-d'œuvre, équipement, sous-traitance ou autre."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!description.trim() || !quantity || addLine.isPending}
          >
            {addLine.isPending ? "Ajout…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Type">
          <Select
            value={lineType}
            onChange={(event) => setLineType(event.target.value as EstimateLineType)}
            aria-label="Type de ligne"
          >
            {LINE_TYPES.map((value) => (
              <option key={value} value={value}>
                {formatEstimateLineType(value)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Poste">
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={isMaterial ? "Cable NMWU 12/2" : "Installation, 2 electriciens"}
            aria-label="Poste"
          />
        </Field>

        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Quantité">
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                aria-label="Quantité"
              />
            </Field>
          </div>
          {isMaterial && (
            <div className="w-32">
              <Field label="Unité">
                <Select
                  value={unit}
                  onChange={(event) => setUnit(event.target.value as UnitCode)}
                  aria-label="Unité"
                >
                  {STOCKABLE_UNITS.map((code) => (
                    <option key={code} value={code}>
                      {formatUnit(code)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Coût unitaire" hint="Laissez vide si le prix n'est pas connu.">
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={unitCost}
                onChange={(event) => setUnitCost(event.target.value)}
                aria-label="Coût unitaire"
              />
            </Field>
          </div>
          {isMaterial && (
            <div className="w-32">
              <Field label="Perte (%)" hint="10 = 10 %.">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="any"
                  inputMode="decimal"
                  value={wastePercent}
                  onChange={(event) => setWastePercent(event.target.value)}
                  aria-label="Pourcentage de perte"
                />
              </Field>
            </div>
          )}
        </div>

        <Field label="Provision" hint="Montant forfaitaire, en plus de la quantité. Facultatif.">
          <Input
            type="number"
            min="0"
            step="any"
            inputMode="decimal"
            value={allowance}
            onChange={(event) => setAllowance(event.target.value)}
            aria-label="Provision"
          />
        </Field>
      </div>
    </Dialog>
  );
}
